import { FC, useState, useEffect } from 'react';
import styled from 'styled-components';
import EpisodeSelector, { TYPES } from './EpisodeSelector';
import QuestionMarkIconSVG from './question-mark-circle.svg';
import Tooltip from './Tooltip';
import TooltipContent from './TooltipContent';
import Dialog from '../Dialog';
import { theme } from '../../theme';
import EpisodeOfCareSection from './EpisodeOfCareSection';

import {
  Section,
  SectionTitle,
  SectionContent,
  Label,
  GrayedText,
  DataTextLarge,
  Dot,
  LongSectionContent,
} from './Components';

import {
  getActiveDays,
  getRemoteTherapyMonitoringPlan,
  updateRemoteTherapyMonitoringPlan,
} from '../../services/RemoteTherapyMonitoring';
import ConferenceDurationSection from './ConferenceDurationSection';

const STATUSES = {
  NOT_STARTED: {
    color: theme.color.grayscale[200],
    text: 'NOT STARTED',
  },
  AT_RISK: {
    color: theme.color.accentPrimary.regular,
    text: 'AT RISK',
  },
  HIGH_RISK: {
    color: theme.color.accentTertiary.regular,
    text: 'HIGH RISK',
  },
  ON_TRACK: {
    color: theme.color.tertiary.regular,
    text: 'ON TRACK',
  },
  COMPLETED: {
    color: theme.color.grayscale[1000],
    text: 'COMPLETED',
    background: theme.color.tertiary.regular,
    removeDot: true,
  },
};

const PLAN_PERIOD = 30;
const DAY_GOAL = 16;
const REQUIRED_PERCENTAGE = 0.51;
const HIGH_RISK_THRESHOLD = 1.3;

const Title = styled.div(({ theme }) => {
  return `
  font-size: ${theme.type.size.large};
  font-weight: ${theme.type.weight.bold};
  margin-bottom: 30px;
`;
});

const QuestionMarkIcon = styled.img`
  display: inline-block;
  width: 17px;
  margin-left: 3px;
`;

const Container = styled.div`
  width: 1200px;
  text-align: left;
  overflow: hidden;

  @media (max-width: 1500px) {
    width: 99%;
  }

  @media (max-width: 800px) {
    width: 100%;
  }
`;

const SectionWrap = styled.div(({ theme }) => {
  return `
    border: 1px solid ${theme.color.grayscale[700]};
    border-radius: 10px;
    padding: 15px;
    margin: 0 auto;
    margin-bottom: 20px;  
  `;
});

const DividerLine = styled.div(({ theme }) => {
  return `
    border-top: 1px solid ${theme.color.grayscale[700]};
    margin-top: 20px;
    margin-bottom: 20px;
  `;
});

const Loader = styled.div(({ theme }) => {
  return `
    font-size: ${theme.type.size.regular};
    text-align: center;
    width: 100%;
    padding: 17px;
  `;
});

const StatusIndicator = styled.div(({ theme }) => {
  return `
    font-size: ${theme.type.size.regular};
    border-radius: 1000px;
    box-shadow: 1px 1px 1px ${theme.color.grayscale[600]};
    display: inline-block;
    padding: 0 10px;
    margin-top: 8px;
  `;
});

type Status = {
  text?: string;
  color?: string;
  background?: string;
  removeDot?: boolean;
};

type Player = {
  name: string;
  id: number;
};

interface RTMOverviewDialogProps {
  player: Player;
  closeCallback: () => void;
}

const RTMOverviewDialog: FC<RTMOverviewDialogProps> = ({ player, closeCallback }) => {
  const [fromDate, setFromDate] = useState<string>('');
  const [toDate, setToDate] = useState<string>('');
  const [daysPassed, setDaysPassed] = useState('');
  const [activeDays, setActiveDays] = useState(0);
  const [status, setStatus] = useState<Status>({});
  const [relevantEpisodeOfCare, setRelevantEpisodeOfCare] = useState(TYPES['Initial Episode']);
  const [recertificationDate, setRecertificationDate] = useState<string>('');
  const [recertificationToDate, setRecertificationToDate] = useState<string>('');
  const [loadingActiveDays, setLoadingActiveDays] = useState<boolean>(false);

  const calculateDaysPassed = (startDate: string) => {
    const currentDate = new Date();
    const differenceInTime = currentDate.getTime() - new Date(startDate).getTime();
    const differenceInDays = Math.ceil(differenceInTime / (1000 * 3600 * 24));

    if (differenceInDays >= 0) {
      setDaysPassed(differenceInDays >= 0 ? `${Math.abs(differenceInDays)}` : '');
    } else {
      setDaysPassed('');
    }
  };
  const updateActiveDays = async () => {
    setLoadingActiveDays(true);

    let relevantStartDate;
    let relevantEndDate;

    if (relevantEpisodeOfCare === TYPES['Initial Episode']) {
      relevantStartDate = fromDate;
      relevantEndDate = toDate;
    } else {
      relevantStartDate = recertificationDate;
      relevantEndDate = recertificationToDate;
    }

    if (relevantEndDate === '' || relevantStartDate === '') {
      setLoadingActiveDays(false);
      return;
    }

    let activeDays = await getActiveDays(player.id, relevantStartDate, relevantEndDate);

    if (!activeDays) {
      activeDays = 0;
    }

    setActiveDays(activeDays);
    setLoadingActiveDays(false);
  };

  const calculateToDate = (startDate: string) => {
    const futureDate = new Date(startDate);

    futureDate.setDate(futureDate.getDate() + PLAN_PERIOD);

    const formattedFutureDate = futureDate.toISOString().split('T')[0];

    return formattedFutureDate;
  };

  const getConfig = async () => {
    const currentPlan = await getRemoteTherapyMonitoringPlan(player.id);

    if (currentPlan && currentPlan.start_date) {
      setFromDate(currentPlan.start_date);
      setToDate(calculateToDate(currentPlan.start_date));
    }

    if (currentPlan && currentPlan.recertification_date) {
      setRecertificationDate(currentPlan.recertification_date);
      setRecertificationToDate(calculateToDate(currentPlan.recertification_date));
    }

    if (currentPlan && currentPlan.episode_of_care) {
      setRelevantEpisodeOfCare(currentPlan.episode_of_care);
    }
  };

  useEffect(() => {
    getConfig();
  }, []);

  const calculateStatus = () => {
    const daysLeft = PLAN_PERIOD - parseInt(daysPassed);
    const activeDaysRequiredForGoal = DAY_GOAL - activeDays;

    if (activeDays >= DAY_GOAL) {
      return STATUSES.COMPLETED;
    } else if (activeDays / parseInt(daysPassed) >= REQUIRED_PERCENTAGE) {
      return STATUSES.ON_TRACK;
    } else if (daysLeft / activeDaysRequiredForGoal < HIGH_RISK_THRESHOLD) {
      return STATUSES.HIGH_RISK;
    } else if (activeDays > 0) {
      return STATUSES.AT_RISK;
    } else {
      return STATUSES.NOT_STARTED;
    }
  };

  useEffect(() => {
    updateActiveDays();

    if (relevantEpisodeOfCare === TYPES['Initial Episode']) {
      calculateDaysPassed(fromDate);
    } else {
      calculateDaysPassed(recertificationDate);
    }
  }, [fromDate, recertificationDate, relevantEpisodeOfCare, toDate, recertificationToDate]);

  useEffect(() => {
    setStatus(calculateStatus());
  }, [activeDays, daysPassed]);

  const handleConfigChange = async (
    newStartDate: string,
    newRecetificationDate: string,
    newRelevantEpisodeOfCare: number
  ) => {
    const newPlanConfig = {
      start_date: newStartDate,
      episode_of_care: newRelevantEpisodeOfCare,
      recertification_date: newRecetificationDate,
    };

    if (newRelevantEpisodeOfCare === TYPES['Initial Episode']) {
      calculateDaysPassed(newStartDate);
    } else {
      calculateDaysPassed(newRecetificationDate);
    }
    await updateRemoteTherapyMonitoringPlan(player.id, newPlanConfig);
  };

  const getFirstName = (fullName: string) => {
    const names = fullName.split(' ');

    return names[0];
  };

  const printStatus = () => {
    if (status.text) {
      const indicatorStyle = {
        color: status?.color,
        background: status?.background,
      };
      return (
        <StatusIndicator style={indicatorStyle}>
          {!status.removeDot && <Dot>•</Dot>}
          {status.text}
        </StatusIndicator>
      );
    } else {
      return null;
    }
  };

  const activeDaysSuffix = activeDays === 1 ? ' day' : ' days';
  const titleText = `${getFirstName(player.name)}'s Remote Therapy Overview`;

  return (
    <Dialog isOpen={true} closeCallback={closeCallback} canClose={true}>
      <Container>
        <Title>{titleText}</Title>

        <SectionTitle>Episode of Care</SectionTitle>

        <SectionWrap>
          <Section>
            <EpisodeOfCareSection
              label="Initial start date"
              selectedDate={fromDate}
              id="initialStartDate"
              onChange={(newStartDate, newEndDate) => {
                setFromDate(newStartDate);
                setToDate(newEndDate);
                handleConfigChange(newStartDate, recertificationDate, relevantEpisodeOfCare);
              }}
            />
          </Section>

          <DividerLine />

          <Section>
            <EpisodeOfCareSection
              label="Recertification date"
              id="recertificationDate"
              selectedDate={recertificationDate}
              onChange={(newStartDate, newEndDate) => {
                setRecertificationDate(newStartDate);
                setRecertificationToDate(newEndDate);
                handleConfigChange(fromDate, newStartDate, relevantEpisodeOfCare);
              }}
            />
          </Section>
        </SectionWrap>

        <SectionTitle>Remote Monitoring</SectionTitle>

        <SectionWrap>
          <EpisodeSelector
            onSelect={(newEpisodeCode) => {
              setRelevantEpisodeOfCare(newEpisodeCode);
              handleConfigChange(fromDate, recertificationDate, newEpisodeCode);
            }}
            activeButton={relevantEpisodeOfCare}
          />

          {!loadingActiveDays && (
            <Section>
              <LongSectionContent>
                <Label>Patient participation</Label>

                <DataTextLarge>
                  {activeDays}
                  {activeDaysSuffix}
                </DataTextLarge>
              </LongSectionContent>

              <LongSectionContent>
                <Label>Goal</Label>

                <DataTextLarge>
                  {DAY_GOAL} days <GrayedText>(out of 30)</GrayedText>
                </DataTextLarge>
              </LongSectionContent>

              <SectionContent>
                <Label>
                  Status{' '}
                  <Tooltip content={<TooltipContent />}>
                    <QuestionMarkIcon src={QuestionMarkIconSVG} />
                  </Tooltip>
                </Label>

                {printStatus()}
              </SectionContent>
            </Section>
          )}

          {loadingActiveDays && (
            <Section>
              <Loader>
                Loading... <i className="fa fa-spin fa-spinner"></i>
              </Loader>
            </Section>
          )}
        </SectionWrap>

        <SectionWrap>
          <ConferenceDurationSection
            playerId={player.id}
            datesArray={[fromDate, toDate, recertificationDate, recertificationToDate]}
          />
        </SectionWrap>
      </Container>
    </Dialog>
  );
};

export default RTMOverviewDialog;
